import Convert from "ansi-to-html";
import DOMPurify from "dompurify";

// Singleton ANSI converter
const ansiConverter = new Convert({
  fg: "#FFF",
  bg: "#000",
  newline: false,
  escapeXML: false,
});

// Check if text contains ANSI escape codes
const ANSI_REGEX = /\x1b\[[0-9;]*m/;

/**
 * Strip HTML tags from text
 */
export function stripHtmlTags(text: string): string {
  if (!text) return "";
  return text.replace(/<[^>]*>/g, "");
}

/**
 * Check if text contains ANSI escape codes
 */
export function hasAnsiCodes(text: string): boolean {
  return ANSI_REGEX.test(text);
}

/**
 * Convert ANSI escape codes to HTML
 */
export function ansiToHtml(text: string): string {
  if (!text) return "";
  // Only convert if there are actual ANSI codes
  if (!hasAnsiCodes(text)) {
    return text;
  }
  return ansiConverter.toHtml(text);
}

/**
 * Check if text is likely JSON (starts with { or [)
 */
export function isLikelyJson(text: string): boolean {
  if (!text) return false;
  const trimmed = text.trim();
  return (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  );
}

/**
 * Format JSON with indentation
 */
export function prettifyJson(text: string): string {
  if (!text) return "";

  if (!isLikelyJson(text)) {
    return text;
  }

  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return text;
  }
}

/**
 * Sanitize HTML to prevent XSS
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["span", "br"],
    ALLOWED_ATTR: ["style", "class"],
  });
}

/**
 * Format log content with full processing pipeline
 */
export function formatLogContent(
  text: string,
  options: { prettifyJson: boolean }
): string {
  if (!text) return "";

  // Step 1: Strip existing HTML tags (from log content)
  let result = stripHtmlTags(text);

  // Step 2: Prettify JSON if enabled
  if (options.prettifyJson) {
    result = prettifyJson(result);
  }

  // Step 3: Convert ANSI codes to HTML
  result = ansiToHtml(result);

  // Step 4: Sanitize the result
  result = sanitizeHtml(result);

  return result;
}

/**
 * Format log content for HTML rendering (with line breaks for prettified JSON)
 */
export function formatLogContentForHtml(
  text: string,
  options: { prettifyJson: boolean }
): string {
  const formatted = formatLogContent(text, options);

  if (options.prettifyJson) {
    // Convert newlines to <br> and spaces to &nbsp; for HTML display
    return formatted
      .replace(/\n/g, "<br>")
      .replace(/  /g, "&nbsp;&nbsp;");
  }

  return formatted;
}
